/**
 * Data hooks for the Error Logs admin feature.
 *
 * Wraps the three GET endpoints under `/api/v1/admin/error-logs/*`:
 *   - paginated list (filterable by `errorType`/`correlationId`)
 *   - detail by id (UUID)
 *   - aggregate stats (totals + counts by errorType)
 *
 * The list endpoint in the OpenAPI spec declares `errorType`,
 * `correlationId`, `page` and `size`. The brief also asked for an
 * `hours` filter, which the backend does not honour on the list route
 * (it only applies to `/stats`) — `hours` stays UI-only, driving the
 * stats card and time-window select, and is *not* forwarded to the
 * list query. Do not add it to the query params below; the backend
 * ignores it.
 */
import { useQuery, type UseQueryResult } from '@tanstack/react-query';
import { api } from '@favelafood/shared/api/client';
import type { components } from '@favelafood/shared/api/generated/api';

type ErrorLogResponse = components['schemas']['ErrorLogResponse'];
type ErrorLogStats = components['schemas']['ErrorLogStatsResponse'];
type ErrorLogPage = components['schemas']['PageResponseErrorLogResponse'];

export interface ErrorLogsListFilters {
  /** Time-window in hours used by the stats card. Not forwarded to list. */
  hours: number;
  page: number;
  size: number;
  errorType?: string;
  correlationId?: string;
}

export const errorLogsKeys = {
  all: ['error-logs'] as const,
  list: (filters: ErrorLogsListFilters) =>
    ['error-logs', 'list', filters] as const,
  detail: (id: string) => ['error-logs', 'detail', id] as const,
  stats: (hours: number) => ['error-logs', 'stats', hours] as const,
};

export function useErrorLogsList(
  filters: ErrorLogsListFilters,
): UseQueryResult<ErrorLogPage, Error> {
  return useQuery<ErrorLogPage, Error>({
    queryKey: errorLogsKeys.list(filters),
    queryFn: async () => {
      const { data, error } = await api.GET('/api/v1/admin/error-logs', {
        params: {
          query: {
            page: filters.page,
            size: filters.size,
            ...(filters.errorType ? { errorType: filters.errorType } : {}),
            ...(filters.correlationId ? { correlationId: filters.correlationId } : {}),
          },
        },
      });
      if (error) throw error as Error;
      return data as ErrorLogPage;
    },
  });
}

export function useErrorLogDetail(
  id: string | undefined,
): UseQueryResult<ErrorLogResponse, Error> {
  return useQuery<ErrorLogResponse, Error>({
    queryKey: errorLogsKeys.detail(id ?? ''),
    enabled: Boolean(id),
    queryFn: async () => {
      const { data, error } = await api.GET('/api/v1/admin/error-logs/{id}', {
        params: { path: { id: id ?? '' } },
      });
      if (error) throw error as Error;
      return data as ErrorLogResponse;
    },
  });
}

export function useErrorLogStats(
  hours: number,
): UseQueryResult<ErrorLogStats, Error> {
  return useQuery<ErrorLogStats, Error>({
    queryKey: errorLogsKeys.stats(hours),
    queryFn: async () => {
      const { data, error } = await api.GET(
        '/api/v1/admin/error-logs/stats',
        { params: { query: { hours } } },
      );
      if (error) throw error as Error;
      return data as ErrorLogStats;
    },
    // Stats endpoint is best-effort — a flake there must not poison the
    // surrounding list view (mirrors DashboardPage's behaviour).
    retry: false,
    throwOnError: false,
  });
}
